import type { MascotPose } from "@/components/brand/Mascot";

/**
 * Warstwa 2 systemu maskotki — semantyczne emocje.
 *
 * Zamiast hardcode'ować pozę (`pose="peace"`), feature code wybiera emocję
 * (`emotion="celebrate"`), a `poseFor()` wybiera odpowiednią pozę z mapy.
 *
 * Wartości:
 * - **konsystencja** — ta sama emocja → ten sam (lub świadomie randomizowany) look
 *   we wszystkich miejscach apki.
 * - **refactor-safe** — żeby zmienić pozę dla "celebrate", edytujesz jeden wpis
 *   w EMOTION_POSES, nie grep po komponentach.
 * - **A/B test** — w przyszłości można randomizować warianty per user i mierzyć
 *   engagement.
 */
export type MascotEmotion =
  | "celebrate"   // duża radość, "Brawo!"
  | "happy"       // lekka radość/OK
  | "proud"       // duma/trofea/level-up
  | "focused"     // skupienie/wykonywanie zadań
  | "curious"     // ciekawość/pomysł/sugestia
  | "saving"      // skarbonka/oszczędności
  | "protective"  // tarcza/bezpieczeństwo
  | "sleepy"      // znudzony/empty-state
  | "sad"         // smutek/rozczarowanie
  | "surprised"   // zaskoczenie/nowa odznaka
  | "idle"        // neutralna baza
  | "outdoor"     // wolny dzień / na zewnątrz
  | "waiting"     // dziecko czeka na zatwierdzenie
  | "thinking";   // zamyślona, "może coś zaproponuję"

/**
 * Mapa emocja → kandydaci poz. Pierwsza poza = domyślna.
 * Dodatkowe pozy dają opcję wariantowania (np. randomizacja per session).
 */
export const EMOTION_POSES: Record<MascotEmotion, readonly MascotPose[]> = {
  celebrate:  ["peace", "winner", "cheer", "celebrateConfetti"],
  happy:      ["happy", "wink"],
  proud:      ["winner", "bag", "proud2"],
  focused:    ["task", "focused2"],
  curious:    ["idea", "reading", "reading2"],
  saving:     ["piggy", "money", "saving2"],
  protective: ["shield"],
  sleepy:     ["sleepy", "sleepyYawn"],
  sad:        ["sadClipboard", "sadTear", "sadHug"],
  surprised:  ["surprise"],
  idle:       ["idle", "phone"],
  outdoor:    ["outdoor"],
  waiting:    ["waiting"],
  thinking:   ["thinking"],
};

/**
 * Wybierz pozę dla emocji. `variant` (deterministyczny seed) pozwala na
 * reprodukowalne losowanie — np. ten sam mascot w tym samym kontekście
 * dostaje tę samą pozę.
 */
export function poseFor(
  emotion: MascotEmotion,
  variant: number = 0,
): MascotPose {
  const poses = EMOTION_POSES[emotion];
  return poses[variant % poses.length];
}
